<template>
  <div class="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col">
    <header class="bg-purple-600 text-white py-6 px-4">
      <div class="max-w-3xl mx-auto">
        <a :href="backUrl" class="text-purple-200 hover:text-white text-sm">&larr; Back</a>
        <h1 class="text-2xl font-bold mt-1">{{ heading || 'Read a Story' }}</h1>
        <p class="text-purple-100 text-sm mt-1">
          {{ subheading || `Level ${level} Capstone` }}
        </p>
      </div>
    </header>

    <main class="flex-1 max-w-3xl mx-auto w-full px-4 py-6">
      <!-- Loading -->
      <div v-if="loading" class="text-center py-16">
        <div class="inline-block animate-spin rounded-full h-8 w-8 border-4 border-purple-200 border-t-purple-600"></div>
        <p class="mt-4 text-gray-500">
          {{ kind === 'dialogue' ? 'Loading a conversation...' : 'Generating your story...' }}
        </p>
      </div>

      <!-- Error -->
      <div v-else-if="error" class="text-center py-12">
        <p class="text-red-500 mb-4">{{ error }}</p>
        <a :href="backUrl" class="text-purple-600 font-medium hover:underline">Back to Home</a>
      </div>

      <!-- Reading Phase -->
      <div v-else-if="phase === 'read'" class="space-y-6">
        <div class="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 class="text-xl font-bold text-gray-900">{{ story.title }}</h2>

          <div class="flex items-center gap-2 flex-wrap mt-3 mb-5">
            <button
              @click="togglePlayAll"
              class="inline-flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-lg border transition-colors"
              :class="playingAll ? 'bg-purple-600 text-white border-purple-600 hover:bg-purple-700' : 'bg-white text-purple-700 border-purple-200 hover:bg-purple-50'"
            >
              <span aria-hidden="true">{{ playingAll ? '❚❚' : '▶' }}</span>
              {{ playingAll ? 'Pause' : 'Play all' }}
            </button>

            <label class="inline-flex items-center gap-1.5 text-sm text-gray-500">
              <span class="sr-only">Read-along speed</span>
              <select
                v-model.number="rate"
                class="text-sm border border-gray-200 rounded-lg px-2 py-1.5 bg-white text-gray-700 focus:border-purple-400 focus:outline-none"
                title="Read-along speed"
              >
                <option :value="0.6">0.6&times;</option>
                <option :value="0.8">0.8&times;</option>
                <option :value="1">1&times;</option>
              </select>
            </label>

            <button
              @click="toggleAll"
              class="text-sm font-medium px-3 py-1.5 rounded-lg border transition-colors ml-auto"
              :class="allRevealed ? 'bg-purple-100 text-purple-700 border-purple-200' : 'bg-gray-100 text-gray-600 border-gray-200 hover:bg-gray-200'"
            >
              {{ allRevealed ? 'Hide English' : 'Show English' }}
            </button>
          </div>

          <div class="space-y-3 text-gray-700 leading-relaxed">
            <div
              v-for="(line, i) in displayLines"
              :key="i"
              :ref="el => setLineRef(el, i)"
              class="flex items-start gap-2 -mx-2 px-2 py-1.5 rounded-lg transition-colors"
              :class="activeLine === i ? 'bg-purple-50 ring-1 ring-purple-200' : ''"
            >
              <AudioButton :src="line.audioUrl" :text="line.vn" :show-slow="true" class="shrink-0" />
              <div class="min-w-0 flex-1">
                <span
                  v-if="line.speaker"
                  class="block text-xs font-semibold uppercase tracking-wide"
                  :class="speakerClass(line.speaker)"
                >{{ line.speaker }}</span>
                <!-- Words are individually tappable, so the Vietnamese can't sit
                     inside the reveal button — nested buttons are invalid and
                     swallow the inner clicks. Reveal is its own control below. -->
                <p class="text-base">
                  <TappableText :tokens="lineTokens[i]" :text="line.vn" :source="storySource" />
                </p>
                <button
                  v-if="line.en"
                  type="button"
                  class="mt-1 text-left"
                  :aria-expanded="isRevealed(i)"
                  :aria-label="isRevealed(i) ? 'Hide English for this line' : 'Reveal English for this line'"
                  @click="toggleLine(i)"
                >
                  <template v-if="isRevealed(i)">
                    <!-- Literal gloss above the natural English: it shows how
                         the line is built before saying what it means. -->
                    <span v-if="line.literal" class="block text-xs text-purple-400 italic">{{ line.literal }}</span>
                    <span class="block text-sm text-gray-400 italic">{{ line.en }}</span>
                  </template>
                  <!-- Collapsed placeholder keeps the line height stable, so
                       revealing one line doesn't shove the rest of the story. -->
                  <span
                    v-else
                    class="block h-2 w-20 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
                    aria-hidden="true"
                  ></span>
                </button>
              </div>
            </div>
          </div>
        </div>

        <div class="text-center">
          <button
            @click="phase = 'quiz'"
            class="bg-purple-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-purple-700 transition-colors"
          >
            Answer Questions
          </button>
        </div>
      </div>

      <!-- Quiz Phase -->
      <div v-else-if="phase === 'quiz'" class="space-y-6">
        <!-- Link back to re-read -->
        <button @click="phase = 'read'" class="text-purple-600 text-sm font-medium hover:underline">
          &larr; Re-read the story
        </button>

        <div
          v-for="(q, qi) in story.questions"
          :key="qi"
          class="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
        >
          <p class="font-semibold text-gray-900 mb-4">{{ qi + 1 }}. {{ q.question }}</p>
          <div class="space-y-2">
            <button
              v-for="(opt, oi) in q.options"
              :key="oi"
              @click="selectAnswer(qi, opt)"
              :class="answerClass(qi, opt, q.correct_answer)"
              class="w-full text-left px-4 py-3 rounded-lg border transition-colors"
            >
              {{ opt }}
            </button>
          </div>
          <p v-if="answers[qi] !== undefined" class="mt-3 text-sm italic text-gray-500">{{ q.explanation }}</p>
        </div>

        <!-- Results summary -->
        <div v-if="allAnswered" class="bg-purple-50 border border-purple-200 rounded-xl p-6 text-center">
          <p class="text-xl font-bold text-purple-800">{{ correctCount }} / {{ story.questions.length }} correct</p>
          <p v-if="quizResult?.xp_earned" class="text-sm font-medium text-purple-700 mt-1">
            +{{ quizResult.xp_earned }} XP earned
            <span v-if="quizResult.stats"> &middot; &#128293; {{ quizResult.stats.current_streak }} day streak</span>
          </p>
          <div class="flex gap-3 justify-center mt-4">
            <button @click="resetQuiz" class="bg-white text-gray-700 border border-gray-300 px-6 py-2 rounded-lg font-medium hover:bg-gray-50">
              Try Again
            </button>
            <a :href="backUrl" class="bg-purple-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-purple-700">
              Back to Home
            </a>
          </div>
        </div>
      </div>
    </main>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue'
import { apiPost } from '../api/client'
import AudioButton from './shared/AudioButton.vue'
import TappableText from './shared/TappableText.vue'
import { prefetchStaticAudio, getStaticAudioUrl } from '../utils/ttsCache'
import { annotateTexts } from '../utils/lookup'

const props = defineProps({
  level: { type: Number, required: true },
  storyUrl: { type: String, required: true },
  backUrl: { type: String, required: true },
  // 'narrative' (a story) or 'dialogue' (a scripted conversation). Same reader,
  // different pool and chrome.
  kind: { type: String, default: 'narrative' },
  heading: { type: String, default: '' },
  subheading: { type: String, default: '' },
})

const story = ref(null)
const loading = ref(true)
const error = ref(null)
const phase = ref('read')
const answers = ref({})

// Reveal state is a set of line indices. The header button is just "reveal all /
// hide all" over the same set, so there's no second source of truth to conflict
// with per-line taps.
const revealed = ref(new Set())

const storyAudioUrlsRaw = ref([])

const characters = computed(() => story.value?.characters ?? [])

// Normalize both formats into { speaker, vn, en, audioUrl } rows.
const displayLines = computed(() => {
  const s = story.value
  if (!s) return []
  if (Array.isArray(s.lines) && s.lines.length) {
    return s.lines.map((ln, i) => ({
      speaker: ln.speaker ?? null,
      vn: ln.vn,
      en: ln.en,
      literal: ln.literal ?? '',
      audioUrl: ln.audio_url ?? storyAudioUrlsRaw.value[i] ?? null,
    }))
  }
  // Legacy flat content: parallel vn/en line arrays, no speakers.
  const vn = s.content?.split('\n').filter(l => l.trim()) ?? []
  const en = s.english_translation?.split('\n').filter(l => l.trim()) ?? []
  return vn.map((line, i) => ({
    speaker: null,
    vn: line,
    en: en[i] ?? '',
    literal: '',
    audioUrl: storyAudioUrlsRaw.value[i] ?? null,
  }))
})

// Tappable-word tokens, one array per line, fetched in a single batch once the
// story loads. Until it resolves TappableText renders the plain line.
const lineTokens = ref([])
const storySource = computed(() => ({
  kind: props.kind === 'dialogue' ? 'conversation' : 'story',
  id: story.value?.id ?? null,
}))

async function loadAnnotations() {
  const lines = displayLines.value.map(l => l.vn)
  if (!lines.length) return
  lineTokens.value = await annotateTexts(lines)
}

function isRevealed(i) {
  return revealed.value.has(i)
}

function toggleLine(i) {
  const next = new Set(revealed.value)
  next.has(i) ? next.delete(i) : next.add(i)
  revealed.value = next
}

const allRevealed = computed(() =>
  displayLines.value.length > 0 && revealed.value.size === displayLines.value.length
)

function toggleAll() {
  revealed.value = allRevealed.value
    ? new Set()
    : new Set(displayLines.value.map((_, i) => i))
}

// --- Continuous read-along -------------------------------------------------
// Plays every line back to back, highlighting and scrolling to the current one.
// `sequenceToken` invalidates an in-flight run when the user pauses, leaves the
// reading phase, or unmounts — otherwise a queued clip would resume playback
// after the stop.
const PLAY_GAP_MS = 350

const playingAll = ref(false)
const activeLine = ref(-1)
const rate = ref(1)

let sequencePlayer = null
let sequenceToken = 0
const lineRefs = ref([])

function setLineRef(el, i) {
  if (el) lineRefs.value[i] = el
}

function stopPlayAll() {
  sequenceToken++
  if (sequencePlayer) {
    sequencePlayer.pause()
    sequencePlayer = null
  }
  playingAll.value = false
  activeLine.value = -1
}

function playClip(url, token) {
  return new Promise((resolve, reject) => {
    const audio = new Audio(url)
    audio.playbackRate = rate.value
    // Keep pitch (and so the tones) intact when slowed, same as AudioButton.
    audio.preservesPitch = true
    audio.webkitPreservesPitch = true
    sequencePlayer = audio
    audio.addEventListener('ended', resolve)
    // A pause from stopPlayAll never fires 'ended', so resolve on it too or the
    // awaiting loop would hang forever holding this element.
    audio.addEventListener('pause', resolve)
    audio.addEventListener('error', reject)
    audio.play().catch(reject)
  }).finally(() => {
    if (token === sequenceToken) sequencePlayer = null
  })
}

function delay(ms) {
  return new Promise(resolve => setTimeout(resolve, ms))
}

async function togglePlayAll() {
  if (playingAll.value) {
    stopPlayAll()
    return
  }

  const token = ++sequenceToken
  playingAll.value = true

  for (let i = 0; i < displayLines.value.length; i++) {
    if (token !== sequenceToken) return
    activeLine.value = i

    const url = displayLines.value[i].audioUrl
    if (url) {
      try {
        await playClip(getStaticAudioUrl(url), token)
      } catch (e) {
        // One missing clip shouldn't end the read-through.
      }
    }

    if (token !== sequenceToken) return
    await delay(PLAY_GAP_MS)
  }

  if (token === sequenceToken) stopPlayAll()
}

// Speed changes apply to the clip already in flight, not just the next one.
watch(rate, (value) => {
  if (sequencePlayer) sequencePlayer.playbackRate = value
})

watch(activeLine, (i) => {
  if (i >= 0) lineRefs.value[i]?.scrollIntoView({ behavior: 'smooth', block: 'center' })
})

// Leaving the reading phase (or the page) must not leave audio playing.
watch(phase, (value) => {
  if (value !== 'read') stopPlayAll()
})
onBeforeUnmount(stopPlayAll)

const SPEAKER_COLORS = ['text-purple-700', 'text-blue-700', 'text-emerald-700', 'text-rose-700', 'text-amber-700', 'text-cyan-700']
function speakerClass(speaker) {
  const idx = characters.value.findIndex(c => c.name === speaker)
  return SPEAKER_COLORS[(idx >= 0 ? idx : 0) % SPEAKER_COLORS.length]
}

const allAnswered = computed(() =>
  story.value && Object.keys(answers.value).length === story.value.questions.length
)

const correctCount = computed(() =>
  story.value?.questions.filter((q, i) => answers.value[i] === q.correct_answer).length ?? 0
)

function selectAnswer(qi, opt) {
  if (answers.value[qi] !== undefined) return
  answers.value = { ...answers.value, [qi]: opt }
}

function answerClass(qi, opt, correct) {
  if (answers.value[qi] === undefined) return 'bg-gray-50 border-gray-200 hover:border-purple-300 hover:bg-purple-50'
  if (opt === correct) return 'bg-emerald-50 border-emerald-400 text-emerald-900'
  if (answers.value[qi] === opt) return 'bg-red-50 border-red-400 text-red-900'
  return 'bg-gray-50 border-gray-200 text-gray-400'
}

function resetQuiz() {
  answers.value = {}
  phase.value = 'read'
}

// Stories count toward XP and the streak: record the quiz once per visit
// (retries within the same visit don't double-award).
const quizResult = ref(null)
const quizRecorded = ref(false)

watch(allAnswered, async (done) => {
  if (!done || quizRecorded.value) return
  quizRecorded.value = true
  try {
    quizResult.value = await apiPost('/api/v1/stories/record_quiz', {
      level: props.level,
      correct: correctCount.value,
      total: story.value.questions.length,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    })
  } catch (e) {
    // XP is a bonus here; the quiz result is already on screen.
  }
})

onMounted(async () => {
  try {
    const data = await apiPost(props.storyUrl, { level: props.level, kind: props.kind })
    if (data.error) {
      error.value = data.error
    } else {
      story.value = data.story
      storyAudioUrlsRaw.value = data.story.audio_urls || []
      const urls = storyAudioUrlsRaw.value.filter(Boolean)
      if (urls.length) prefetchStaticAudio(urls)
      loadAnnotations()
    }
  } catch (e) {
    error.value = "We couldn't load this story just now. Try again in a moment."
  } finally {
    loading.value = false
  }
})
</script>
